import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Loader2, Save, Mail, Paperclip, Server, Send } from "lucide-react";
import { toast } from "sonner";

export default function AdminEmailSettings() {
  const [data, setData] = useState({ notify_email: "", notify_on_contact: true, notify_on_registration: true });
  const [saving, setSaving] = useState(false);
  const [maxMb, setMaxMb] = useState(10);
  const [savingUpload, setSavingUpload] = useState(false);

  const [smtp, setSmtp] = useState({ enabled: false, host: "", port: 587, username: "", password: "", from_email: "", use_tls: true, use_ssl: false, has_password: false });
  const [savingSmtp, setSavingSmtp] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    api.get("/email-settings").then((r) => setData(r.data)).catch(() => {});
    api.get("/upload-settings").then((r) => setMaxMb(r.data.max_file_mb || 10)).catch(() => {});
    api.get("/smtp-settings").then((r) => setSmtp((s) => ({ ...s, ...r.data, password: "" }))).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/email-settings", data);
      toast.success("Ustawienia e-mail zapisane");
    } catch (e) {
      toast.error(e.response?.data?.detail?.[0]?.msg || "Błąd zapisu — sprawdź adres e-mail");
    } finally { setSaving(false); }
  };

  const saveUpload = async () => {
    setSavingUpload(true);
    try {
      await api.put("/upload-settings", { max_file_mb: Number(maxMb) });
      toast.success("Limit załączników zapisany");
    } catch (e) {
      toast.error("Błąd zapisu — podaj wartość od 1 do 100 MB");
    } finally { setSavingUpload(false); }
  };

  const saveSmtp = async () => {
    setSavingSmtp(true);
    try {
      await api.put("/smtp-settings", { ...smtp, port: Number(smtp.port) });
      toast.success("Ustawienia SMTP zapisane");
      const r = await api.get("/smtp-settings");
      setSmtp((s) => ({ ...s, ...r.data, password: "" }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Błąd zapisu ustawień SMTP");
    } finally { setSavingSmtp(false); }
  };

  const sendTest = async () => {
    if (!testTo) { toast.error("Podaj adres, na który wysłać test"); return; }
    setTesting(true);
    try {
      const r = await api.post("/smtp-settings/test", { to: testTo });
      toast.success(r.data.message || "Wysłano test");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Nie udało się wysłać testu");
    } finally { setTesting(false); }
  };

  return (
    <div>
      <h2 className="font-display text-2xl font-bold text-ztk-navy dark:text-white">Powiadomienia e-mail</h2>
      <p className="mt-1 text-sm text-muted-foreground">Ustaw adres, na który mają przychodzić powiadomienia ze strony.</p>

      <div className="mt-8 max-w-xl space-y-5 rounded-2xl border border-border bg-card p-6">
        <div>
          <Label className="flex items-center gap-2"><Mail className="h-4 w-4 text-ztk-blue" /> Twój adres e-mail do powiadomień</Label>
          <Input type="email" value={data.notify_email} onChange={(e) => setData({ ...data, notify_email: e.target.value })} data-testid="email-notify-address" className="mt-1.5" placeholder="np. ztk@ztkig.pl" />
        </div>

        <div className="flex items-center justify-between rounded-lg border border-border p-4">
          <div>
            <p className="font-medium text-ztk-navy dark:text-white">Nowa wiadomość kontaktowa</p>
            <p className="text-sm text-muted-foreground">Otrzymasz e-mail, gdy ktoś wyśle formularz kontaktowy.</p>
          </div>
          <Switch checked={data.notify_on_contact} onCheckedChange={(v) => setData({ ...data, notify_on_contact: v })} data-testid="email-toggle-contact" />
        </div>

        <div className="flex items-center justify-between rounded-lg border border-border p-4">
          <div>
            <p className="font-medium text-ztk-navy dark:text-white">Nowa rejestracja członka</p>
            <p className="text-sm text-muted-foreground">Otrzymasz e-mail, gdy zarejestruje się nowy użytkownik (do zatwierdzenia).</p>
          </div>
          <Switch checked={data.notify_on_registration} onCheckedChange={(v) => setData({ ...data, notify_on_registration: v })} data-testid="email-toggle-registration" />
        </div>

        <Button onClick={save} disabled={saving} className="gap-1.5 bg-ztk-blue" data-testid="email-settings-save">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Zapisz ustawienia
        </Button>
      </div>

      {/* SMTP */}
      <h2 className="mt-12 font-display text-2xl font-bold text-ztk-navy dark:text-white">Serwer poczty (SMTP)</h2>
      <p className="mt-1 text-sm text-muted-foreground">Skonfiguruj własny serwer SMTP, aby wiadomości (rejestracje, potwierdzenia, reset hasła, newsletter) wychodziły bezpośrednio z Twojego serwera.</p>

      <div className="mt-6 max-w-xl space-y-5 rounded-2xl border border-border bg-card p-6">
        <div className="flex items-center justify-between rounded-lg border border-border p-4">
          <div>
            <p className="flex items-center gap-2 font-medium text-ztk-navy dark:text-white"><Server className="h-4 w-4 text-ztk-blue" /> Wysyłaj przez SMTP</p>
            <p className="text-sm text-muted-foreground">Gdy włączone, wszystkie e-maile idą przez ten serwer.</p>
          </div>
          <Switch checked={smtp.enabled} onCheckedChange={(v) => setSmtp({ ...smtp, enabled: v })} data-testid="smtp-enabled" />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="sm:col-span-2">
            <Label>Host SMTP</Label>
            <Input value={smtp.host} onChange={(e) => setSmtp({ ...smtp, host: e.target.value })} data-testid="smtp-host" className="mt-1.5" placeholder="np. smtp.gmail.com" />
          </div>
          <div>
            <Label>Port</Label>
            <Input type="number" value={smtp.port} onChange={(e) => setSmtp({ ...smtp, port: e.target.value })} data-testid="smtp-port" className="mt-1.5" placeholder="587" />
          </div>
        </div>

        <div>
          <Label>Adres nadawcy (From)</Label>
          <Input type="email" value={smtp.from_email} onChange={(e) => setSmtp({ ...smtp, from_email: e.target.value })} data-testid="smtp-from" className="mt-1.5" placeholder="np. no-reply@ztkig.pl" />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <Label>Login</Label>
            <Input value={smtp.username} onChange={(e) => setSmtp({ ...smtp, username: e.target.value })} data-testid="smtp-username" className="mt-1.5" placeholder="użytkownik / e-mail" autoComplete="off" />
          </div>
          <div>
            <Label>Hasło</Label>
            <Input type="password" value={smtp.password} onChange={(e) => setSmtp({ ...smtp, password: e.target.value })} data-testid="smtp-password" className="mt-1.5" placeholder={smtp.has_password ? "•••••••• (bez zmian)" : "hasło SMTP"} autoComplete="new-password" />
          </div>
        </div>

        <div className="flex flex-wrap gap-6">
          <div className="flex items-center gap-2">
            <Switch checked={smtp.use_tls} onCheckedChange={(v) => setSmtp({ ...smtp, use_tls: v })} data-testid="smtp-tls" />
            <Label className="cursor-pointer">STARTTLS (port 587)</Label>
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={smtp.use_ssl} onCheckedChange={(v) => setSmtp({ ...smtp, use_ssl: v })} data-testid="smtp-ssl" />
            <Label className="cursor-pointer">SSL/TLS (port 465)</Label>
          </div>
        </div>

        <Button onClick={saveSmtp} disabled={savingSmtp} className="gap-1.5 bg-ztk-blue" data-testid="smtp-save">
          {savingSmtp ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Zapisz SMTP
        </Button>

        <div className="rounded-lg border border-dashed border-border p-4">
          <Label className="flex items-center gap-2"><Send className="h-4 w-4 text-ztk-blue" /> Wyślij wiadomość testową</Label>
          <p className="mb-2 mt-1 text-xs text-muted-foreground">Najpierw zapisz ustawienia powyżej, potem wyślij test.</p>
          <div className="flex gap-2">
            <Input type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} data-testid="smtp-test-to" placeholder="adres@przyklad.pl" />
            <Button onClick={sendTest} disabled={testing} variant="outline" className="shrink-0 gap-1.5" data-testid="smtp-test-send">
              {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Wyślij test
            </Button>
          </div>
        </div>
      </div>

      <h2 className="mt-12 font-display text-2xl font-bold text-ztk-navy dark:text-white">Załączniki na forum</h2>
      <p className="mt-1 text-sm text-muted-foreground">Maksymalny rozmiar pliku (zdjęcia i PDF), jaki członkowie mogą dodawać do postów.</p>
      <div className="mt-6 max-w-xl space-y-4 rounded-2xl border border-border bg-card p-6">
        <div>
          <Label className="flex items-center gap-2"><Paperclip className="h-4 w-4 text-ztk-blue" /> Maksymalny rozmiar pliku (MB)</Label>
          <Input type="number" min={1} max={100} value={maxMb} onChange={(e) => setMaxMb(e.target.value)} data-testid="upload-max-mb" className="mt-1.5 w-40" />
        </div>
        <Button onClick={saveUpload} disabled={savingUpload} className="gap-1.5 bg-ztk-blue" data-testid="upload-settings-save">
          {savingUpload ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Zapisz limit
        </Button>
      </div>
    </div>
  );
}
